export class UnsupportedValueError extends Error {
    constructor(msg, value) {
        super(`${msg}: ${value}`);
    }
}
// The IC error codes we make decisions on. These do not map onto distinct reject codes - a frozen
// canister is `SysTransient` and one with no wasm module is `CanisterError`, both of which are
// retryable in general - so the specific code has to be used to tell them apart.
// See https://internetcomputer.org/docs/references/execution-errors
export const ICErrorCode = {
    CanisterOutOfCycles: "IC0207", // the canister is frozen
    CanisterNotFound: "IC0301", // the canister has been deleted
    CanisterInstructionLimitExceeded: "IC0522", // the call ran out of instructions
    CanisterMethodNotFound: "IC0536", // the canister has no such method, eg. it hasn't been upgraded
    CanisterWasmModuleNotFound: "IC0537", // the canister has been uninstalled
};
export class HttpError extends Error {
    code;
    // The IC error code of the underlying rejection, eg. "IC0301", where the failure was a
    // rejection which carried one. Populated by `toCanisterResponseError`.
    rejectErrorCode;
    constructor(code, error) {
        super(error.message);
        this.code = code;
        this.stack = error.stack;
        this.name = "HttpError";
    }
}
export class NoMeetingToJoin extends Error {
}
export class AuthError extends HttpError {
    code;
    constructor(code, error) {
        super(code, error);
        this.code = code;
        this.name = "AuthError";
    }
}
// Errors lose their prototype when they cross the worker boundary (they are serialised with
// `JSON.stringify`), so on the client side they can only be recognised by `name`. These constants
// keep the names the classes set below and the checks which read them in a single place.
export const SESSION_EXPIRY_ERROR_NAME = "SessionExpiryError";
export const INVALID_DELEGATION_ERROR_NAME = "InvalidDelegationError";
export class SessionExpiryError extends HttpError {
    code;
    constructor(code, error) {
        super(code, error);
        this.code = code;
        this.name = SESSION_EXPIRY_ERROR_NAME;
    }
}
export class DestinationInvalidError extends HttpError {
    constructor(error) {
        super(404, error);
        this.name = "DestinationInvalidError";
    }
}
// The canister exists but cannot serve the call right now - it is frozen (out of cycles) or has no
// wasm module (uninstalled). Unlike `DestinationInvalidError` this does not mean the canister is
// gone: it may recover once it is topped up or reinstalled. But it cannot recover within the
// lifetime of a single request, so retrying here only stalls the caller.
export class CanisterUnavailableError extends HttpError {
    constructor(error) {
        super(503, error);
        this.name = "CanisterUnavailableError";
    }
}
// Whether the call failed because the canister is gone for good. A deleted group's or community's
// canister is left uninstalled rather than deleted, but either means the same here.
export function isCanisterGoneError(error) {
    return (error instanceof DestinationInvalidError ||
        (error instanceof CanisterUnavailableError &&
            error.rejectErrorCode === ICErrorCode.CanisterWasmModuleNotFound));
}
// The canister has no such method, eg. because it hasn't yet been upgraded to a version which has
// it. Retrying won't change that within the lifetime of a request.
export class CanisterMethodNotFoundError extends HttpError {
    constructor(error) {
        super(501, error);
        this.name = "CanisterMethodNotFoundError";
    }
}
// The canister ran out of instructions handling the call. A retry runs it over much the same
// state, so would only run out again.
export class InstructionLimitExceededError extends HttpError {
    constructor(error) {
        super(500, error);
        this.name = "InstructionLimitExceededError";
    }
}
export class ResponseTooLargeError extends HttpError {
    size;
    maxSize;
    constructor(error, size, maxSize) {
        super(500, error);
        this.size = size;
        this.maxSize = maxSize;
        this.name = "ResponseTooLargeError";
    }
}
export class InvalidDelegationError extends HttpError {
    constructor(error) {
        super(403, error);
        this.name = INVALID_DELEGATION_ERROR_NAME;
    }
}
export class TypeboxValidationError extends Error {
    constructor(error) {
        super();
        this.name = "TypeboxValidationError";
        this.message = error?.message ?? "";
        this.stack = error?.stack;
    }
}
// We'll use this is the front end tries to do something that the anonymous user should not be able to do
export class AnonymousOperationError extends Error {
    constructor() {
        super();
        this.name = "AnonymousOperationError";
    }
}
export var ErrorCode;
(function (ErrorCode) {
    ErrorCode[ErrorCode["Unknown"] = 0] = "Unknown";
    // NotAuthorized
    ErrorCode[ErrorCode["InitiatorNotFound"] = 100] = "InitiatorNotFound";
    ErrorCode[ErrorCode["InitiatorNotAuthorized"] = 101] = "InitiatorNotAuthorized";
    ErrorCode[ErrorCode["InitiatorSuspended"] = 102] = "InitiatorSuspended";
    ErrorCode[ErrorCode["InitiatorNotInChat"] = 103] = "InitiatorNotInChat";
    ErrorCode[ErrorCode["InitiatorNotInCommunity"] = 104] = "InitiatorNotInCommunity";
    ErrorCode[ErrorCode["InitiatorLapsed"] = 105] = "InitiatorLapsed";
    ErrorCode[ErrorCode["InitiatorBlocked"] = 106] = "InitiatorBlocked";
    ErrorCode[ErrorCode["BotNotAuthenticated"] = 107] = "BotNotAuthenticated";
    // Invalid
    ErrorCode[ErrorCode["ChatNotFound"] = 200] = "ChatNotFound";
    ErrorCode[ErrorCode["MessageNotFound"] = 201] = "MessageNotFound";
    ErrorCode[ErrorCode["CommunityNotFound"] = 202] = "CommunityNotFound";
    ErrorCode[ErrorCode["ThreadNotFound"] = 203] = "ThreadNotFound";
    ErrorCode[ErrorCode["CanisterNotFound"] = 204] = "CanisterNotFound";
    ErrorCode[ErrorCode["TargetUserNotFound"] = 205] = "TargetUserNotFound";
    ErrorCode[ErrorCode["SwapNotFound"] = 206] = "SwapNotFound";
    ErrorCode[ErrorCode["BotNotFound"] = 207] = "BotNotFound";
    ErrorCode[ErrorCode["OwnerNotFound"] = 208] = "OwnerNotFound";
    ErrorCode[ErrorCode["NewOwnerNotFound"] = 209] = "NewOwnerNotFound";
    ErrorCode[ErrorCode["InvalidRequest"] = 210] = "InvalidRequest";
    ErrorCode[ErrorCode["InvalidReaction"] = 211] = "InvalidReaction";
    ErrorCode[ErrorCode["InvalidPrincipal"] = 212] = "InvalidPrincipal";
    ErrorCode[ErrorCode["InvalidAvatar"] = 213] = "InvalidAvatar";
    ErrorCode[ErrorCode["InvalidEndpoint"] = 214] = "InvalidEndpoint";
    ErrorCode[ErrorCode["InvalidFlags"] = 215] = "InvalidFlags";
    ErrorCode[ErrorCode["InvalidTerm"] = 216] = "InvalidTerm";
    ErrorCode[ErrorCode["InvalidLanguage"] = 217] = "InvalidLanguage";
    ErrorCode[ErrorCode["InvalidAccessGate"] = 218] = "InvalidAccessGate";
    ErrorCode[ErrorCode["NameTooShort"] = 219] = "NameTooShort";
    ErrorCode[ErrorCode["NameTooLong"] = 220] = "NameTooLong";
    ErrorCode[ErrorCode["TermTooShort"] = 221] = "TermTooShort";
    ErrorCode[ErrorCode["TermTooLong"] = 222] = "TermTooLong";
    ErrorCode[ErrorCode["DescriptionTooLong"] = 223] = "DescriptionTooLong";
    ErrorCode[ErrorCode["AlreadyRegistered"] = 224] = "AlreadyRegistered";
    ErrorCode[ErrorCode["PrincipalAlreadyUsed"] = 225] = "PrincipalAlreadyUsed";
    ErrorCode[ErrorCode["AlreadyAdded"] = 226] = "AlreadyAdded";
    ErrorCode[ErrorCode["AlreadySet"] = 227] = "AlreadySet";
    ErrorCode[ErrorCode["AlreadyAwarded"] = 228] = "AlreadyAwarded";
    ErrorCode[ErrorCode["AlreadyLifetimeDiamondMember"] = 229] = "AlreadyLifetimeDiamondMember";
    ErrorCode[ErrorCode["AlreadyReported"] = 230] = "AlreadyReported";
    ErrorCode[ErrorCode["OwnerSuspended"] = 231] = "OwnerSuspended";
    ErrorCode[ErrorCode["NewOwnerSuspended"] = 232] = "NewOwnerSuspended";
    ErrorCode[ErrorCode["ChatFrozen"] = 233] = "ChatFrozen";
    ErrorCode[ErrorCode["CommunityFrozen"] = 234] = "CommunityFrozen";
    ErrorCode[ErrorCode["InsufficientFunds"] = 235] = "InsufficientFunds";
    ErrorCode[ErrorCode["PinRequired"] = 236] = "PinRequired";
    ErrorCode[ErrorCode["PinIncorrect"] = 237] = "PinIncorrect";
    ErrorCode[ErrorCode["TooManyFailedPinAttempts"] = 238] = "TooManyFailedPinAttempts";
    ErrorCode[ErrorCode["UserLimitReached"] = 239] = "UserLimitReached";
    ErrorCode[ErrorCode["CannotBlockSelf"] = 240] = "CannotBlockSelf";
    ErrorCode[ErrorCode["CannotBlockUser"] = 241] = "CannotBlockUser";
    ErrorCode[ErrorCode["CommunityPublic"] = 242] = "CommunityPublic";
    ErrorCode[ErrorCode["CommunityNotPublic"] = 243] = "CommunityNotPublic";
    ErrorCode[ErrorCode["TargetUserNotInCommunity"] = 244] = "TargetUserNotInCommunity";
    ErrorCode[ErrorCode["TargetUserNotInChat"] = 245] = "TargetUserNotInChat";
    ErrorCode[ErrorCode["NotInitialized"] = 246] = "NotInitialized";
    ErrorCode[ErrorCode["Expired"] = 247] = "Expired";
    ErrorCode[ErrorCode["DelegationTooOld"] = 248] = "DelegationTooOld";
    ErrorCode[ErrorCode["MalformedSignature"] = 249] = "MalformedSignature";
    ErrorCode[ErrorCode["PriceMismatch"] = 250] = "PriceMismatch";
    ErrorCode[ErrorCode["NameTaken"] = 251] = "NameTaken";
    ErrorCode[ErrorCode["TooManyCommands"] = 252] = "TooManyCommands";
    ErrorCode[ErrorCode["NotDiamondMember"] = 253] = "NotDiamondMember";
    ErrorCode[ErrorCode["UnexpectedIndex"] = 254] = "UnexpectedIndex";
    ErrorCode[ErrorCode["NoChange"] = 255] = "NoChange";
    ErrorCode[ErrorCode["CyclesBalanceTooLow"] = 257] = "CyclesBalanceTooLow";
    ErrorCode[ErrorCode["AlreadyInProgress"] = 258] = "AlreadyInProgress";
    ErrorCode[ErrorCode["InvalidPublicKey"] = 259] = "InvalidPublicKey";
    ErrorCode[ErrorCode["InvalidReferralCode"] = 260] = "InvalidReferralCode";
    ErrorCode[ErrorCode["ReferralCodeAlreadyClaimed"] = 261] = "ReferralCodeAlreadyClaimed";
    ErrorCode[ErrorCode["ReferralCodeExpired"] = 262] = "ReferralCodeExpired";
    ErrorCode[ErrorCode["UsernameTooShort"] = 263] = "UsernameTooShort";
    ErrorCode[ErrorCode["UsernameTooLong"] = 264] = "UsernameTooLong";
    ErrorCode[ErrorCode["InvalidUsername"] = 265] = "InvalidUsername";
    ErrorCode[ErrorCode["DisplayNameTooShort"] = 266] = "DisplayNameTooShort";
    ErrorCode[ErrorCode["DisplayNameTooLong"] = 267] = "DisplayNameTooLong";
    ErrorCode[ErrorCode["InvalidDisplayName"] = 268] = "InvalidDisplayName";
    ErrorCode[ErrorCode["NotInvited"] = 269] = "NotInvited";
    ErrorCode[ErrorCode["CurrencyNotSupported"] = 270] = "CurrencyNotSupported";
    ErrorCode[ErrorCode["InvalidRoleChange"] = 271] = "InvalidRoleChange";
    ErrorCode[ErrorCode["CannotRemoveSelf"] = 272] = "CannotRemoveSelf";
    ErrorCode[ErrorCode["CannotTipSelf"] = 273] = "CannotTipSelf";
    ErrorCode[ErrorCode["NameReserved"] = 274] = "NameReserved";
    ErrorCode[ErrorCode["RulesTooShort"] = 275] = "RulesTooShort";
    ErrorCode[ErrorCode["RulesTooLong"] = 276] = "RulesTooLong";
    ErrorCode[ErrorCode["AvatarTooBig"] = 277] = "AvatarTooBig";
    ErrorCode[ErrorCode["RecipientMismatch"] = 278] = "RecipientMismatch";
    ErrorCode[ErrorCode["NewRegistrationsClosed"] = 279] = "NewRegistrationsClosed";
    ErrorCode[ErrorCode["LastOwnerCannotLeave"] = 280] = "LastOwnerCannotLeave";
    ErrorCode[ErrorCode["TooManyInvites"] = 281] = "TooManyInvites";
    ErrorCode[ErrorCode["CommunityRulesNotAccepted"] = 282] = "CommunityRulesNotAccepted";
    ErrorCode[ErrorCode["ChatRulesNotAccepted"] = 283] = "ChatRulesNotAccepted";
    ErrorCode[ErrorCode["InvalidExternalUrl"] = 284] = "InvalidExternalUrl";
    ErrorCode[ErrorCode["VideoCallAlreadyEnded"] = 285] = "VideoCallAlreadyEnded";
    ErrorCode[ErrorCode["TargetUserBlocked"] = 286] = "TargetUserBlocked";
    ErrorCode[ErrorCode["MessageIdAlreadyExists"] = 287] = "MessageIdAlreadyExists";
    ErrorCode[ErrorCode["ReplicaNotUpToDate"] = 288] = "ReplicaNotUpToDate";
    ErrorCode[ErrorCode["InvalidMessageContent"] = 289] = "InvalidMessageContent";
    ErrorCode[ErrorCode["TransferCannotBeZero"] = 290] = "TransferCannotBeZero";
    ErrorCode[ErrorCode["NoActiveStreak"] = 291] = "NoActiveStreak";
    ErrorCode[ErrorCode["TextTooLong"] = 292] = "TextTooLong";
    ErrorCode[ErrorCode["MessageHardDeleted"] = 293] = "MessageHardDeleted";
    ErrorCode[ErrorCode["InvalidMessageType"] = 294] = "InvalidMessageType";
    ErrorCode[ErrorCode["TooManyUsers"] = 296] = "TooManyUsers";
    ErrorCode[ErrorCode["VideoCallNotFound"] = 297] = "VideoCallNotFound";
    ErrorCode[ErrorCode["PrizeNotFound"] = 298] = "PrizeNotFound";
    ErrorCode[ErrorCode["PrizeEnded"] = 299] = "PrizeEnded";
    ErrorCode[ErrorCode["PrizeFullyClaimed"] = 300] = "PrizeFullyClaimed";
    ErrorCode[ErrorCode["PrizeAlreadyClaimed"] = 301] = "PrizeAlreadyClaimed";
    ErrorCode[ErrorCode["PrizeLedgerError"] = 302] = "PrizeLedgerError";
    ErrorCode[ErrorCode["InvalidName"] = 303] = "InvalidName";
    ErrorCode[ErrorCode["UserGroupNotFound"] = 304] = "UserGroupNotFound";
    ErrorCode[ErrorCode["ChatNotPublic"] = 305] = "ChatNotPublic";
    ErrorCode[ErrorCode["PollNotFound"] = 306] = "PollNotFound";
    ErrorCode[ErrorCode["PollEnded"] = 307] = "PollEnded";
    ErrorCode[ErrorCode["PollOptionNotFound"] = 308] = "PollOptionNotFound";
    ErrorCode[ErrorCode["CannotChangeVote"] = 309] = "CannotChangeVote";
    ErrorCode[ErrorCode["AlreadyImportingIntoAnotherCommunity"] = 310] = "AlreadyImportingIntoAnotherCommunity";
    ErrorCode[ErrorCode["InvalidSignature"] = 311] = "InvalidSignature";
    ErrorCode[ErrorCode["TransferFailed"] = 312] = "TransferFailed";
    ErrorCode[ErrorCode["GroupAlreadyBeingImported"] = 313] = "GroupAlreadyBeingImported";
    ErrorCode[ErrorCode["BannerTooBig"] = 314] = "BannerTooBig";
    ErrorCode[ErrorCode["CannotChangeRoleOfLastOwner"] = 315] = "CannotChangeRoleOfLastOwner";
    ErrorCode[ErrorCode["CannotMakeBotOwner"] = 316] = "CannotMakeBotOwner";
    ErrorCode[ErrorCode["MessageAlreadyFinalized"] = 317] = "MessageAlreadyFinalized";
    ErrorCode[ErrorCode["MaxGroupsCreated"] = 318] = "MaxGroupsCreated";
    ErrorCode[ErrorCode["MaxCommunitiesCreated"] = 319] = "MaxCommunitiesCreated";
    ErrorCode[ErrorCode["Throttled"] = 320] = "Throttled";
    ErrorCode[ErrorCode["InvalidChannelName"] = 321] = "InvalidChannelName";
    ErrorCode[ErrorCode["ApprovalFailed"] = 322] = "ApprovalFailed";
    ErrorCode[ErrorCode["TransferCannotBeToSelf"] = 323] = "TransferCannotBeToSelf";
    ErrorCode[ErrorCode["DateInThePast"] = 324] = "DateInThePast";
    ErrorCode[ErrorCode["SwapFailed"] = 325] = "SwapFailed";
    ErrorCode[ErrorCode["PinTooShort"] = 326] = "PinTooShort";
    ErrorCode[ErrorCode["PinTooLong"] = 327] = "PinTooLong";
    ErrorCode[ErrorCode["SwapStatusOpen"] = 328] = "SwapStatusOpen";
    ErrorCode[ErrorCode["SwapStatusCancelled"] = 329] = "SwapStatusCancelled";
    ErrorCode[ErrorCode["SwapStatusExpired"] = 330] = "SwapStatusExpired";
    ErrorCode[ErrorCode["SwapStatusReserved"] = 331] = "SwapStatusReserved";
    ErrorCode[ErrorCode["SwapStatusAccepted"] = 332] = "SwapStatusAccepted";
    ErrorCode[ErrorCode["SwapStatusCompleted"] = 333] = "SwapStatusCompleted";
    ErrorCode[ErrorCode["NoEligibleNeurons"] = 334] = "NoEligibleNeurons";
    ErrorCode[ErrorCode["ProposalNotFound"] = 335] = "ProposalNotFound";
    ErrorCode[ErrorCode["ProposalNotAcceptingVotes"] = 336] = "ProposalNotAcceptingVotes";
    ErrorCode[ErrorCode["IdentityLinkRequestNotFound"] = 337] = "IdentityLinkRequestNotFound";
    ErrorCode[ErrorCode["InvalidBotActionScope"] = 338] = "InvalidBotActionScope";
    ErrorCode[ErrorCode["WebhookNotFound"] = 339] = "WebhookNotFound";
    ErrorCode[ErrorCode["InvalidWebhook"] = 340] = "InvalidWebhook";
    ErrorCode[ErrorCode["InvalidOriginatingCanister"] = 341] = "InvalidOriginatingCanister";
    ErrorCode[ErrorCode["LinkingCodeNotFound"] = 342] = "LinkingCodeNotFound";
    ErrorCode[ErrorCode["MaxLinkedIdentitiesLimitReached"] = 343] = "MaxLinkedIdentitiesLimitReached";
    ErrorCode[ErrorCode["ItemNotFound"] = 344] = "ItemNotFound";
    ErrorCode[ErrorCode["AmountNotSpecified"] = 345] = "AmountNotSpecified";
    ErrorCode[ErrorCode["ProfileBackgroundTooBig"] = 346] = "ProfileBackgroundTooBig";
    ErrorCode[ErrorCode["LedgerNotFound"] = 347] = "LedgerNotFound";
    ErrorCode[ErrorCode["PrizeUserNotElligible"] = 348] = "PrizeUserNotElligible";
    ErrorCode[ErrorCode["NoEventsToDelete"] = 349] = "NoEventsToDelete";
    ErrorCode[ErrorCode["InsufficientAllowance"] = 350] = "InsufficientAllowance";
    ErrorCode[ErrorCode["LimitReached"] = 354] = "LimitReached";
    ErrorCode[ErrorCode["UserMovedToNewSubnet"] = 355] = "UserMovedToNewSubnet";
    // InternalError
    ErrorCode[ErrorCode["C2CError"] = 500] = "C2CError";
    // Impossible (supposedly)
    ErrorCode[ErrorCode["Impossible"] = 600] = "Impossible";
})(ErrorCode || (ErrorCode = {}));
